import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';

@Component({
    selector: 'add-mcc-partners',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputComponent],
    templateUrl: './add-mcc-partner.component.html',
    styleUrls: ['./add-mcc-partner.component.scss']
})
export class AddEventMCCPartnersComponent implements OnInit, OnDestroy {
    @Input() initialData: any = null;
    @Output() formSubmitted = new EventEmitter<any>();
    @Output() formValidation = new EventEmitter<boolean>();

    form!: FormGroup;
    private dialogConfirmListener?: (event: Event) => void;

    customErrorMessages = {
        partnerName: { required: 'Please enter a partner name.' },
        contactName: {
            required: 'Please enter a lastname.',
            whitespaceOnly: 'Please enter a valid contact name.'
        },
        contactEmail: { required: 'Please enter a valid primary email.' },
        contactPhone: {
            required: 'Please enter a phone number.',
            pattern: 'Please enter a valid phone number.',
            minlength: 'Phone number must be at least 10 digits.',
            maxlength: 'Phone number cannot exceed 10 digits.'
        },
    };

    constructor(private fb: FormBuilder) { }

    ngOnInit(): void {
        this.initForm();
        this.setupDialogListener();
    }

    ngOnDestroy(): void {
        this.cleanupDialogListener();
    }

    private initForm(): void {
        this.form = this.fb.group({
            partnerName: [this.initialData ? this.initialData['partner name'] ?? this.initialData.partnerName ?? '' : '', requiredTrimmed],
            contactName: [
                this.initialData ? this.initialData['contact person'] ?? this.initialData.contactPerson ?? '' : '',
                [this.noWhitespaceOnlyValidator.bind(this)]
            ],
            contactEmail: [this.initialData?.contactEmail || this.initialData?.Email, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )],
            // contactPhone: [this.initialData?.contactPhone || this.initialData?.Phone, [Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)]]
            contactPhone: [
                this.initialData?.contactPhone || this.initialData?.Phone || '',
                [
                    Validators.pattern(/^(?:$|(?:0[47]\d{8}|\+61 ?[47]\d{8}))$/)
                ]
            ]
        });

        this.formValidation.emit(this.form.valid);
        this.form.valueChanges.subscribe(() => {
            this.formValidation.emit(this.form.valid);
        });
    }

    private noWhitespaceOnlyValidator(control: AbstractControl): { [key: string]: any } | null {
        if (control.value === null || control.value === undefined || control.value === '') {
            return null;
        }

        if (control.value.trim().length === 0) {
            return { whitespaceOnly: true };
        }
        return null;
    }

    private setupDialogListener(): void {
        this.dialogConfirmListener = () => this.handleDialogConfirm();
        document.addEventListener('dialogConfirm', this.dialogConfirmListener);
    }

    private cleanupDialogListener(): void {
        if (this.dialogConfirmListener) {
            document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
        }
    }

    handleDialogConfirm(): void {
        if (this.form.valid) {
            const formValue = this.form.value;
            if (formValue.contactName) {
                formValue.contactName = formValue.contactName.trim();
            }
            this.formSubmitted.emit(formValue);
        } else {
            this.markFormAsTouched();
        }
    }

    onSubmit(): void {
        this.handleDialogConfirm();
    }

    private markFormAsTouched(): void {
        this.form.markAllAsTouched();
    }

    isFormValid(): boolean {
        return this.form.valid;
    }
}